
import { memo } from 'react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Trash2, AlertTriangle } from 'lucide-react';

export interface DeleteTarget {
    type: 'node' | 'edge';
    id: string;
    label?: string;
    nodeType?: string;
    /** Number of connected edges that will also be deleted (for nodes) */
    connectedEdges?: number;
}

interface DeleteConfirmationDialogProps {
    /** Items to be deleted */
    targets: DeleteTarget[];
    /** Whether the dialog is open */
    open: boolean;
    /** Callback when dialog is closed */
    onOpenChange: (open: boolean) => void;
    /** Callback when deletion is confirmed */
    onConfirm: () => void;
    /** Whether deletion is in progress */
    isDeleting?: boolean;
}

export const DeleteConfirmationDialog = memo(function DeleteConfirmationDialog({
    targets,
    open,
    onOpenChange,
    onConfirm,
    isDeleting = false,
}: DeleteConfirmationDialogProps) {
    const nodeCount = targets.filter((t) => t.type === 'node').length;
    const edgeCount = targets.filter((t) => t.type === 'edge').length;
    const totalConnectedEdges = targets
        .filter((t) => t.type === 'node')
        .reduce((sum, t) => sum + (t.connectedEdges ?? 0), 0);

    const getTitle = () => {
        if (nodeCount > 0 && edgeCount > 0) {
            return `Delete ${nodeCount} state${nodeCount > 1 ? 's' : ''} and ${edgeCount} connection${edgeCount > 1 ? 's' : ''}?`;
        }
        if (nodeCount > 0) {
            return nodeCount === 1
                ? `Delete "${targets[0].label || targets[0].id}"?`
                : `Delete ${nodeCount} states?`;
        }
        return edgeCount === 1 ? 'Delete this connection?' : `Delete ${edgeCount} connections?`;
    };

    const getDescription = () => {
        const parts: string[] = [];

        if (nodeCount > 0) {
            parts.push(
                nodeCount === 1
                    ? `This will remove the ${targets[0].nodeType || 'state'} "${targets[0].label || targets[0].id}" from the workflow.`
                    : `This will remove ${nodeCount} states from the workflow.`
            );

            if (totalConnectedEdges > 0) {
                parts.push(
                    `${totalConnectedEdges} connected transition${totalConnectedEdges > 1 ? 's' : ''} will also be removed.`
                );
            }
        }

        if (edgeCount > 0 && nodeCount === 0) {
            parts.push(
                edgeCount === 1
                    ? 'This will remove the connection between these states.'
                    : `This will remove ${edgeCount} connections between states.`
            );
        }

        parts.push('This action cannot be undone.');

        return parts.join(' ');
    };

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        {getTitle()}
                    </AlertDialogTitle>
                    <AlertDialogDescription>{getDescription()}</AlertDialogDescription>
                </AlertDialogHeader>

                {/* Show list of items being deleted if multiple */}
                {targets.length > 1 && targets.length <= 5 && (
                    <div className="my-2 rounded-md bg-slate-50 p-3 text-sm">
                        <p className="mb-2 font-medium text-slate-700">Items to delete:</p>
                        <ul className="space-y-1">
                            {targets.map((target) => (
                                <li
                                    key={`${target.type}-${target.id}`}
                                    className="flex items-center gap-2 text-slate-600"
                                >
                                    <span
                                        className={target.type === 'node' ? 'text-blue-500' : 'text-slate-400'}
                                    >
                                        {target.type === 'node' ? '■' : '─'}
                                    </span>
                                    {target.label || target.id}
                                    {target.nodeType && (
                                        <span className="text-xs text-slate-400">({target.nodeType})</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <AlertDialogFooter>
                    <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={onConfirm}
                        disabled={isDeleting}
                        className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                    >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
});

